import {
  foldContactSearchAscii,
  fuzzyTokenSimilarity,
} from "./contactSearchFuzzy.js";

const GIVEN_NAMES_DE = new Set(
  [
    "aaron",
    "adam",
    "adrian",
    "alex",
    "alexander",
    "alexandra",
    "alice",
    "alina",
    "amelie",
    "andreas",
    "anna",
    "anne",
    "anton",
    "antonia",
    "ben",
    "benedikt",
    "benjamin",
    "bernd",
    "birgit",
    "carla",
    "carl",
    "carolin",
    "caroline",
    "charlotte",
    "chris",
    "christian",
    "christina",
    "christoph",
    "clara",
    "clemens",
    "daniel",
    "david",
    "dennis",
    "dominik",
    "elias",
    "elisa",
    "elisabeth",
    "ella",
    "emil",
    "emilia",
    "emma",
    "erik",
    "eva",
    "fabian",
    "felix",
    "finn",
    "florian",
    "franz",
    "franziska",
    "friedrich",
    "georg",
    "hanna",
    "hannah",
    "hans",
    "heidi",
    "helena",
    "henri",
    "henry",
    "jakob",
    "jan",
    "jannik",
    "johann",
    "johanna",
    "jonas",
    "jonathan",
    "julia",
    "julian",
    "justus",
    "karl",
    "karla",
    "katharina",
    "katja",
    "lara",
    "laura",
    "lea",
    "lena",
    "leo",
    "leon",
    "leonie",
    "liam",
    "lina",
    "lisa",
    "louis",
    "luisa",
    "lukas",
    "marc",
    "marcel",
    "marco",
    "maria",
    "marie",
    "marius",
    "markus",
    "martin",
    "matthias",
    "max",
    "maximilian",
    "mia",
    "michael",
    "mika",
    "mikael",
    "moritz",
    "nina",
    "noah",
    "ole",
    "oliver",
    "oskar",
    "paul",
    "paula",
    "peter",
    "philipp",
    "rafael",
    "raphael",
    "robert",
    "sarah",
    "sara",
    "sebastian",
    "simon",
    "sofia",
    "sophia",
    "sophie",
    "stefan",
    "susanne",
    "timo",
    "tim",
    "tobias",
    "tom",
    "valentin",
    "victoria",
    "viktoria",
    "vincent",
  ].map((name) => foldContactSearchAscii(name)),
);

function asStr(v) {
  return String(v ?? "").trim();
}

function normalizePersonNameInput(input) {
  return asStr(input).replace(/\s+/g, " ");
}

export function tokenizePersonNameInput(input) {
  return normalizePersonNameInput(input)
    .split(/\s+/)
    .map((part) => part.replace(/[^\p{L}'-]/gu, "").trim())
    .filter(Boolean);
}

export function isLikelyGermanGivenName(part) {
  const folded = foldContactSearchAscii(part);
  if (!folded) return false;
  if (GIVEN_NAMES_DE.has(folded)) return true;
  for (const name of GIVEN_NAMES_DE) {
    if (folded.length >= 4 && fuzzyTokenSimilarity(folded, name) >= 0.9) {
      return true;
    }
  }
  return false;
}

export function formatPersonDisplayName(firstName, lastName) {
  return [asStr(firstName), asStr(lastName)].filter(Boolean).join(" ");
}

export function analyzePersonNameInput(input) {
  const original = normalizePersonNameInput(input);
  const tokens = tokenizePersonNameInput(original);
  if (tokens.length === 0) {
    return {
      ok: false,
      reason: "empty",
      original,
      tokens,
      firstName: "",
      lastName: "",
      requiresConfirmation: false,
      confidence: "none",
    };
  }
  if (tokens.length === 1) {
    return {
      ok: true,
      original,
      tokens,
      firstName: tokens[0],
      lastName: "",
      requiresConfirmation: false,
      confidence: "single",
    };
  }

  const firstLikely = isLikelyGermanGivenName(tokens[0]);
  const secondLikely = isLikelyGermanGivenName(tokens[1]);
  const shouldSwap = !firstLikely && secondLikely;
  const firstName = shouldSwap ? tokens[1] : tokens[0];
  const lastName = shouldSwap
    ? [tokens[0], ...tokens.slice(2)].join(" ")
    : tokens.slice(1).join(" ");

  return {
    ok: true,
    original,
    tokens,
    firstName,
    lastName,
    requiresConfirmation: true,
    confidence: firstLikely !== secondLikely ? "given-name" : "ambiguous",
    suggestedOrder: shouldSwap ? "last-first" : "first-last",
  };
}

export function scorePersonDisplayNameSimilarity(input, person) {
  const parsed = analyzePersonNameInput(input);
  const personFirst = asStr(person?.first_name);
  const personLast = asStr(person?.last_name);
  const queryFull = foldContactSearchAscii(parsed.original);
  const full = foldContactSearchAscii(
    formatPersonDisplayName(personFirst, personLast),
  );
  const swapped = foldContactSearchAscii(
    formatPersonDisplayName(personLast, personFirst),
  );
  if (!queryFull || !full) return 0;
  if (queryFull === full || queryFull === swapped) return 1;

  const qTokens = tokenizePersonNameInput(parsed.original).map(
    foldContactSearchAscii,
  );
  const pTokens = tokenizePersonNameInput(full).map(foldContactSearchAscii);
  if (!qTokens.length || !pTokens.length) return 0;

  let sum = 0;
  for (const q of qTokens) {
    let best = 0;
    for (const p of pTokens) {
      best = Math.max(best, fuzzyTokenSimilarity(q, p));
    }
    sum += best;
  }
  const tokenScore = sum / qTokens.length;
  const fullScore = Math.max(
    fuzzyTokenSimilarity(queryFull, full),
    fuzzyTokenSimilarity(queryFull, swapped),
  );
  return Math.max(tokenScore, fullScore);
}
